import { rgba } from '@vimeo/bokeh-utils';
import { darken } from 'color2k';

const basicBadge = (color, hover = true) => {
  return {
    color,
    backgroundColor: rgba(color, 0.15),
    ...hover && {
      _hover: {
        color: darken(color, 0.1),
        backgroundColor: rgba(color, 0.2)
      }
    }
  };
};
const Badge = {
  baseStyle: {
    fontSize: "text-xs",
    textTransform: "uppercase",
    width: "max-content",
    height: "max-content"
  },
  variants: {
    alum: {
      color: "#503873",
      backgroundColor: rgba("#503873", 0.25)
    },
    business: {
      ...basicBadge("#28A878")
    },
    curation: {
      color: "#ff8a00",
      backgroundColor: rgba("#FFB21E", 0.15),
      _hover: {
        color: darken("#FF8A00", 0.1),
        backgroundColor: rgba("#FFB21E", 0.25)
      }
    },
    live: {
      color: "white",
      background: "#ff4d4d",
      _hover: {
        color: "white",
        background: "red.600"
      }
    },
    "not-yet-rated": {
      color: "slate.900",
      border: "1px solid",
      borderColor: "slate.900",
      _dark: {
        color: "rgb(144, 144, 144)",
        borderColor: "rgb(144, 144, 144)"
      }
    },
    "live-archive": {
      color: "#8699a6",
      border: "1px solid #8699a6"
    },
    mature: {
      color: "red.500",
      borderColor: "red.500",
      border: "1px solid"
    },
    new: {
      color: "red.500",
      verticalAlign: "top"
    },
    plus: {
      border: "none",
      ...basicBadge("#00adef")
    },
    pro: {
      ...basicBadge("#23313b")
    },
    producer: {
      border: "none",
      ...basicBadge("#503873")
    },
    sponsor: {
      ...basicBadge("#28A878")
    },
    staff: {
      color: "#ff8a00",
      backgroundColor: rgba("#FFB21E", 0.15),
      _hover: {
        color: darken("#FF8A00", 0.1),
        backgroundColor: rgba("#FFB21E", 0.25)
      }
    },
    support: {
      color: "#ff8a00",
      backgroundColor: rgba("#FFB21E", 0.15),
      _hover: {
        color: darken("#FF8A00", 0.1),
        backgroundColor: rgba("#FFB21E", 0.25)
      }
    },
    upgrade: {
      color: "white",
      backgroundColor: "blue.500",
      _hover: {
        color: "white",
        backgroundColor: "blue.600"
      }
    },
    // Bokeh Variants
    "default-outline": {
      bg: "transparent",
      color: "text-secondary",
      borderColor: "text-secondary"
    },
    "mature-outline": {
      bg: "transparent",
      color: "status-destructive-primary",
      borderColor: "status-destructive-primary"
    },
    success: {
      bg: "status-positive-primary",
      borderColor: "status-positive-primary",
      color: "white"
    },
    error: {
      bg: "status-destructive-primary",
      borderColor: "status-destructive-primary",
      color: "white"
    },
    warning: {
      bg: "status-caution-primary",
      borderColor: "status-caution-primary",
      color: "white"
    },
    info: {
      bg: "blue.400",
      borderColor: "blue.400",
      color: "white"
    },
    neutral: {
      bg: "gray.500",
      borderColor: "gray.500",
      color: "white"
    },
    "timestamp-blur": {
      bg: "grayAlpha.100",
      borderColor: "transparent",
      backdropFilter: "blur(var(--vimeo-blur-md))",
      "::-moz-backdrop-filter": "blur(var(--vimeo-blur-md))",
      color: "text-primary",
      _dark: {
        bg: "grayAlpha.600"
      }
    },
    timestamp: {
      bg: "darkBlueAlpha.300",
      borderColor: "transparent",
      backdropFilter: "blur(var(--vimeo-blur-md))",
      "::-moz-backdrop-filter": "blur(var(--vimeo-blur-md))",
      color: "text-primary",
      _dark: {
        bg: "lightBlueAlpha.500"
      }
    }
  },
  sizes: {
    // Bokeh size - xs (mapping to sm, there is no xs in Iris)
    xs: {
      padding: "0.1875rem 0.25rem",
      fontSize: "0.5625rem",
      // 9px
      fontWeight: "medium",
      lineHeight: 1.2
    },
    sm: {
      padding: "0.1875rem 0.25rem",
      fontSize: "0.5625rem",
      // 9px
      fontWeight: "medium",
      lineHeight: 1.2
    },
    lg: {
      padding: "50",
      fontSize: "text",
      fontWeight: "medium",
      lineHeight: 0.8
    },
    xl: {
      borderWidth: "2px",
      borderRadius: "0.25rem",
      padding: "0.5rem 0.75rem",
      fontWeight: "bold",
      lineHeight: 0.8,
      fontSize: "text-lg"
    }
  },
  defaultProps: {
    variant: "default",
    size: "sm"
  }
};

export { Badge, basicBadge };
